<template>
  <div class="w-full p-5 md:w-128 pt-16 sm:pt-5" data-testid="viewer-templates-modal">
    <!-- Show upselling for free users -->
    <TemplatesUpselling v-if="isFree" />

    <!-- Show templates for paying users -->
    <template v-else>
      <h2 class="text-2xl font-semibold text-black dark:text-zinc-200 mb-2">Select template</h2>
      <p class="text-sm text-slate-600 dark:text-zinc-400 mb-6">
        Work faster with templates! Quickly apply pre-defined settings around your content (customization, security and more!). Set up one from scratch or get
        started with one below.
      </p>

      <!-- Loading skeleton -->
      <TemplatesSkeleton v-if="templateStore.isLoading" />

      <!-- Templates content -->
      <Tabs v-else :selected-tab="selectedTab">
        <Tab id="default-templates" name="Default templates">
          <DefaultTemplatesList
            :is-template-configured="templateManager.isTemplateConfigured"
            :get-button-text="templateManager.getButtonText"
            :get-button-variant="templateManager.getButtonVariant"
            :get-button-icon="templateManager.getButtonIcon"
            @configure="templateManager.handleConfigure"
            @apply="templateManager.handleApplyTemplate"
          />
        </Tab>

        <Tab id="custom-templates" name="Custom templates">
          <CustomTemplatesList
            :custom-templates="customTemplates"
            :is-template-owner="templateManager.isTemplateOwner"
            @create-new="templateManager.handleCreateNew"
            @toggle-default="templateManager.handleToggleDefault"
            @configure="templateManager.handleConfigure"
            @apply="templateManager.handleApplyTemplate"
          />
        </Tab>
      </Tabs>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, computed, ref } from 'vue';
import { Tabs, Tab } from '@/storybook/tabs';
import { useViewerTemplateStore } from '../../store/useViewerTemplateStore';
import { useTemplateManager } from '../../composables/useTemplateManager';
import DefaultTemplatesList from './DefaultTemplatesList.vue';
import CustomTemplatesList from './CustomTemplatesList.vue';
import TemplatesSkeleton from './TemplatesSkeleton.vue';
import TemplatesUpselling from './TemplatesUpselling.vue';

const props = withDefaults(
  defineProps<{
    isFree?: boolean;
  }>(),
  {
    isFree: false,
  }
);

const templateStore = useViewerTemplateStore();
const templateManager = useTemplateManager();

// Tab management similar to useSubheaderTabs pattern
const selectedTab = ref('#default-templates');

// Use store refs for reactivity - customTemplates is a getter
const customTemplates = computed(() => templateStore.customTemplates);

const updateSelectedTab = () => {
  // If custom templates exist, switch to custom templates tab
  if (customTemplates.value.length > 0) {
    selectedTab.value = '#custom-templates';
  } else {
    selectedTab.value = '#default-templates';
  }
};

onMounted(async () => {
  // Only fetch templates for paying users
  if (!props.isFree) {
    await templateStore.fetchTemplates();
    // Set initial tab based on whether custom templates exist
    updateSelectedTab();
  }
});

// Watch for custom templates changes to auto-switch tabs
watch(
  customTemplates,
  () => {
    updateSelectedTab();
  },
  { immediate: true }
);
</script>

<style scoped>
:deep(.tabs-component) {
  @apply md:gap-6;
}
</style>
